import { Arrow } from "./../geometry/arrow";
import { Geometry } from "../geometry/geometry";
import { Group } from "../geometry/group";
import { applyTransformations } from "./utils";
/**
 * Renderer class to take scene geometry and render it to 2d
 * polygon coordinates.
 *
 * 1. Takes a scene and camera
 * 2. converts the scene 3d geometry to 2d screen geometry based on the camera
 * 3. tries to render each face in order from furthest from camera to closest
 * 4. finally, draws the lines (arrows) over top of everything
 *
 * Implementers need just implement
 *   - drawPolygon - a method that draws polygons on some 2d graphics area
 *   - drawArrow - a method that draws an arrow
 *   - onBeforeRender - do any prep work necessary before rendering a frame
 *   - onComplete - handle any final logic
 */
export class PolygonRenderer {
    constructor() {
        this.polygons = [];
        this.arrows = [];
    }
    render(scene, camera) {
        this.polygons = [];
        scene.objects.forEach((object) => {
            this.renderObject3D(object, camera, []);
        });
        this.onBeforeRender();
        this.renderPolygons();
        this.renderArrows();
        this.onComplete();
    }
    renderPolygons() {
        this.polygons.sort((a, b) => {
            return a.centroid.z - b.centroid.z;
        });
        this.polygons.forEach((p) => this.drawPolygon(p));
    }
    renderArrows() {
        this.arrows.forEach(({ p1, p2, uid }) => {
            this.drawArrow(p1, p2, uid);
        });
    }
    renderObject3D(object, camera, transformations) {
        if (object instanceof Geometry) {
            this.renderGeometry(object, camera, transformations);
        }
        else if (object instanceof Arrow) {
            this.renderArrow(object, camera, transformations);
        }
        else if (object instanceof Group) {
            let group = object;
            // let sorted = this.sortObjects(group.objects, camera, [
            //   group.matrix,
            //   ...transformations,
            // ]);
            group.objects.forEach((object) => {
                this.renderObject3D(object, camera, [group.matrix, ...transformations]);
            });
        }
    }
    renderGeometry(object, camera, transformations) {
        // this.sortFaces(object.faces, object, transformations);
        object.faces.forEach((face) => {
            let points = [];
            face.indices
                .map((index) => object.vertices[index])
                .forEach((vertex) => {
                let objectToScreen = [
                    object.matrix,
                    ...transformations,
                    camera.matrix,
                ];
                let screenPoint = applyTransformations(vertex, objectToScreen);
                // Need to flip y to look correct on svg viewbox
                screenPoint.multiply(1, -1, 1);
                points.push(screenPoint);
            });
            this.addPolygon(points, face, object, transformations);
        });
    }
    renderArrow(object, camera, transformations) {
        let objectToScreen = [object.matrix, ...transformations, camera.matrix];
        let p1Screen = applyTransformations(object.p1, objectToScreen);
        let p2Screen = applyTransformations(object.p2, objectToScreen);
        this.arrows.push({ p1: p1Screen, p2: p2Screen, uid: object.uid });
    }
    addPolygon(points, face, object, transformations) {
        this.polygons.push({
            points,
            face,
            object,
            centroid: applyTransformations(face.centroid, [
                object.matrix,
                ...transformations,
            ]),
        });
    }
    sortObjects(objects, camera, transformations) {
        let sorted = [...objects];
        sorted.sort((a, b) => {
            let aToWorld = [a.matrix, ...transformations];
            let bToWorld = [b.matrix, ...transformations];
            let aCentroid = applyTransformations(a.centroid, aToWorld);
            let bCentroid = applyTransformations(b.centroid, bToWorld);
            // TODO actually use camera, currently only sorting by Z
            return aCentroid.z - bCentroid.z;
        });
        return sorted;
    }
}
